/**
 * Saremi Academy Date & Timezone Utilities
 * Standardized on Asia/Kolkata (IST: UTC+05:30) for all calendar and scheduling operations.
 */

/**
 * Returns a standardized date string (YYYY-MM-DD) in Indian Standard Time (Asia/Kolkata).
 * Handles null/undefined (defaults to current IST date), Date objects, epoch numbers, and ISO date strings.
 */
export function getISTDateString(date?: Date | string | number | null): string {
  if (!date) {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }
  if (typeof date === 'string') {
    const trimmed = date.trim();
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    }
    return trimmed;
  }
  const d = typeof date === 'number' ? new Date(date) : date;
  if (d instanceof Date && !isNaN(d.getTime())) {
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/**
 * Returns today's date in Asia/Kolkata (IST) as YYYY-MM-DD.
 */
export function getTodayISTDateString(): string {
  return getISTDateString();
}

/**
 * Formats a Date or ISO string into a human-readable Indian Standard Time date.
 * Example: 24 Sep 2026
 */
export function formatISTDate(date?: Date | string | number | null): string {
  if (!date) return '';
  const dateStr = getISTDateString(date);
  try {
    const [y, m, d] = dateStr.split('-');
    const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    return dt.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

/**
 * Computes offset date in days in IST (e.g. +7 days, -1 day) as YYYY-MM-DD.
 */
export function getOffsetISTDateString(daysOffset: number, baseDate?: Date | string | number | null): string {
  const base = baseDate ? (typeof baseDate === 'string' ? new Date(baseDate) : typeof baseDate === 'number' ? new Date(baseDate) : baseDate) : new Date();
  const offsetDate = new Date(base.getTime() + daysOffset * 86400000);
  return getISTDateString(offsetDate);
}
